<template>
  <waves-background />
  <div class="container">
    <!--  API ERROR  -->
    <div
      v-if="apiError"
      class="w-100"
    >
      <div class="mx-auto justify-center d-block d-sm-flex mb-10">
        <div class="code">{{ apiError.message }}</div>
        <div class="text-code text-sm-left d-flex d-sm-block justify-space-between">
          <div>[{{ apiError.code }}]</div>
          <div>API ERROR</div>
        </div>
      </div>

      <p class="sub-title my-2">
        [{{ apiError.method?.toUpperCase() || 'no method' }}]{{ apiError.apiUrl || '–' }}
      </p>

      <p class="sub-title my-2">requestId: {{ apiError.requestId || '–' }}</p>
      <p class="sub-title my-2">errorCode: {{ apiError?.apiErrorCode || '–' }}</p>

      <p class="title my-4">{{ error.message }}</p>
      <div class="trace my-2 mx-2">{{ error.stack }}</div>
    </div>

    <!--  CLIENT ERRORS  -->
    <div
      v-else
      class="w-100"
    >
      <div class="mx-auto justify-center d-block d-sm-flex mt-4 mb-10">
        <div class="code">{{ HttpStatus[error?.statusCode || 400] }}</div>
        <div class="text-code text-sm-left d-flex d-sm-block justify-space-between">
          <div>[{{ error?.statusCode || 400 }}]</div>
          <div>CLIENT ERROR</div>
        </div>
      </div>

      <p class="title my-2">
        {{
          error?.message ||
          'При загрузке возникла необработанная ошибка, обратитесь в службу поддержки'
        }}
      </p>
      <div class="trace my-2 mx-2">{{ error.stack }}</div>
    </div>

    <v-btn
      class="font-weight-bold my-4"
      color="primary"
      :text="redirectLink.label"
      @click="redirect()"
    />
  </div>
</template>

<script setup lang="ts">
import type { NuxtError } from '#app';
import type { ApiException } from '~/shared/errors/ApiException';
import { HttpStatus } from '~/shared/enums/HttpStatus';

export type ApiErrorData = {
  error: {
    data: ApiException;
  };
};

export type ClientErrorData = {
  redirectLink?: {
    label: string;
    to: string;
  };
};

const { error } = defineProps<{ error: NuxtError<ClientErrorData | ApiErrorData> }>();

const isApiError = (error?.data as ApiException)?.isApiException;
const apiError = isApiError && (error?.data as ApiException);

const clientError = !isApiError ? (error as NuxtError<ClientErrorData>) : undefined;

const redirectLink = computed(
  () =>
    (clientError && (error as NuxtError<ClientErrorData>).data?.redirectLink) || {
      label: 'Вернуться домой',
      to: '/',
    },
);

useHead({
  title: `${error.message}`,
  meta: [{ name: 'robots', content: 'noindex, nofollow' }],
});

function redirect() {
  clearError({ redirect: redirectLink.value.to });
}
</script>

<style lang="scss" scoped>
.code {
  font-weight: 200;
  font-size: 100px;
  line-height: 120px;
  text-align: center;
  color: #1e598f;
  overflow-wrap: anywhere;

  @include respond-to(mobile) {
    font-size: 50px;
    line-height: 50px;
  }
}

.text-code {
  font-weight: 200;
  font-size: 40px;
  line-height: 60px;
  text-align: center;
  color: #ce598f;

  @include respond-to(mobile) {
    font-size: 30px;
    line-height: 30px;
  }
}

.title {
  font-weight: 400;
  font-size: 32px;
  line-height: 32px;
  text-align: center;
  text-wrap: wrap;
  color: #1e598f;

  @include respond-to(mobile) {
    font-size: 20px;
    line-height: 20px;
  }
}

.sub-title {
  font-weight: 400;
  font-size: 24px;
  line-height: 24px;
  text-align: center;
  text-wrap: wrap;
  color: #1e598f;

  @include respond-to(mobile) {
    font-size: 16px;
    line-height: 16px;
  }
}

.trace {
  max-height: 100px;
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  text-align: center;
  color: #1e598f;
  text-wrap: wrap;
  text-overflow: ellipsis;
  overflow: auto;

  @include respond-to(mobile) {
    font-size: 10px;
    line-height: 12px;
    max-height: 200px;
  }
}

.container {
  position: absolute;
  height: 80vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  padding: 16px;

  align-items: center;
  justify-content: center;

  @media (max-height: 500px) {
    height: 100%;
    justify-content: unset;
  }
}
</style>
